import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import { CloudAssetCluster, OptimizationMetrics } from '@cast/types-inventory';
import { toBig, toNumOrZero } from '@cast/utils';

export type DiscoveredClusterSnapshotMetrics = {
  idleCpuPct: number;
  avgUtilizationPct: number;
  monthlySavings: number;
};

const safeDivide = (numerator: number, denominator: number): number =>
  denominator ? numerator / denominator : 0;

const getCostPerMonth = (
  amount?: number | string,
  hourlyCostPerUnit?: number | string
): number =>
  Math.max(
    toBig(amount)
      .times(toBig(hourlyCostPerUnit))
      .times(AVERAGE_HOURS_IN_MONTH)
      .toNumber(),
    0
  );

const getProvisionedMonthlyCost = (metrics?: OptimizationMetrics): number => {
  if (!metrics) {
    return 0;
  }

  let cost = toBig(0);

  if (metrics.cpu?.provisioned) {
    cost = cost.add(
      toBig(
        getCostPerMonth(
          metrics.cpu.provisioned.amount,
          metrics.cpu.provisioned.costPerUnit
        )
      )
    );
  }

  if (metrics.memory?.provisioned) {
    cost = cost.add(
      toBig(
        getCostPerMonth(
          metrics.memory.provisioned.amount,
          metrics.memory.provisioned.costPerUnit
        )
      )
    );
  }

  if (metrics.gpu?.provisioned) {
    cost = cost.add(
      toBig(
        getCostPerMonth(
          metrics.gpu.provisioned.amount,
          metrics.gpu.provisioned.costPerUnit
        )
      )
    );
  }

  return cost.toNumber();
};

export const getDiscoveredClusterSnapshotMetrics = (
  cluster: CloudAssetCluster
): DiscoveredClusterSnapshotMetrics | undefined => {
  const current = cluster.optimization?.metrics;
  const optimized = cluster.optimization?.optimizationMetrics;

  const provisioned = toNumOrZero(current?.cpu?.provisioned?.amount);
  if (!provisioned) {
    return undefined;
  }

  const requested = toNumOrZero(current?.cpu?.requested?.amount);
  const used = toNumOrZero(current?.cpu?.used?.amount);

  const avgUtilizationPct =
    used > 0
      ? safeDivide(used, provisioned) * 100
      : safeDivide(requested, provisioned) * 100;

  const idleCpuPct =
    used > 0
      ? safeDivide(provisioned - used, provisioned) * 100
      : safeDivide(provisioned - requested, provisioned) * 100;

  const currentMonthlyCost = getProvisionedMonthlyCost(current);
  const optimizedMonthlyCost = getProvisionedMonthlyCost(optimized);
  const monthlySavings = Math.max(currentMonthlyCost - optimizedMonthlyCost, 0);

  return {
    idleCpuPct,
    avgUtilizationPct,
    monthlySavings,
  };
};
